import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/apiHelper';

const AdminVideoUpload = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    youtubeUrl: '',
    description: '',
    displayOrder: 0,
    isActive: true,
    videoType: 'TUTORIAL' // NEW: Add video type field
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('ALL'); // NEW: Add filter for video type

  // API base URL - Update this to match your backend
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/video-tutorials`;

  // Fetch videos based on filter
  const fetchVideos = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/admin`;
      
      // If filtering by type, use the specific endpoint
      if (filterType !== 'ALL') {
        url = `${API_BASE_URL}/admin/${filterType.toLowerCase()}`;
      }

      const response = await apiRequest(url, {
        method: 'GET',
      });

      if (response.ok) {
        const data = await response.json();
        setVideos(Array.isArray(data) ? data : []);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setMessage({ type: 'error', text: 'Failed to fetch videos. Please check if the backend is running.' });
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Validate YouTube URL and extract embed ID
  const validateYouTubeUrl = (url) => {
    if (!url || url.trim() === '') {
      return { isValid: false, embedId: '', error: 'YouTube URL is required' };
    }

    // Clean up the URL
    const cleanUrl = url.trim();
    
    // YouTube URL patterns (including YouTube Shorts)
    const patterns = [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of patterns) {
      const match = cleanUrl.match(pattern);
      if (match && match[1]) {
        return { isValid: true, embedId: match[1], error: '' };
      }
    }

    return { isValid: false, embedId: '', error: 'Invalid YouTube URL format' };
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    if (!formData.youtubeUrl.trim()) {
      setMessage({ type: 'error', text: 'YouTube URL is required' });
      return;
    }

    if (!formData.videoType) {
      setMessage({ type: 'error', text: 'Video type is required' });
      return;
    }

    // Validate YouTube URL
    const validation = validateYouTubeUrl(formData.youtubeUrl);
    if (!validation.isValid) {
      setMessage({ type: 'error', text: validation.error });
      return;
    }

    setSubmitting(true);
    
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE_URL}/${editingId}` : API_BASE_URL;

      const requestData = {
        title: formData.title.trim(),
        youtubeUrl: formData.youtubeUrl.trim(),
        description: formData.description.trim(),
        displayOrder: parseInt(formData.displayOrder) || 0,
        isActive: formData.isActive,
        videoType: formData.videoType, // NEW: Include video type
        embedId: validation.embedId
      };

      const response = await apiRequest(url, {
        method: method,
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        const result = await response.json();
        setMessage({ 
          type: 'success', 
          text: editingId ? 'Video updated successfully!' : 'Video added successfully!' 
        });
        resetForm();
        await fetchVideos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ 
          type: 'error', 
          text: errorData.message || `Failed to save video (${response.status})` 
        });
      }
    } catch (error) {
      console.error('Error saving video:', error);
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      const response = await apiRequest(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Video deleted successfully!' });
        await fetchVideos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to delete video' });
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (id) => {
    try {
      const response = await apiRequest(`${API_BASE_URL}/${id}/toggle-status`, {
        method: 'PATCH',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Status updated successfully!' });
        await fetchVideos();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.message || 'Failed to update status' });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    }
  };

  // Handle edit
  const handleEdit = (video) => {
    setFormData({
      title: video.title || '',
      youtubeUrl: video.youtubeUrl || '',
      description: video.description || '',
      displayOrder: video.displayOrder || 0,
      isActive: video.isActive !== undefined ? video.isActive : true,
      videoType: video.videoType || 'TUTORIAL' // NEW: Include video type
    });
    setEditingId(video.id);
    setShowForm(true);
    setMessage({ type: '', text: '' });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      youtubeUrl: '',
      description: '',
      displayOrder: 0,
      isActive: true,
      videoType: 'TUTORIAL' // NEW: Reset video type
    });
    setEditingId(null);
    setShowForm(false);
    setMessage({ type: '', text: '' });
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilterType(newFilter);
    setMessage({ type: '', text: '' });
  };

  // Scroll the form into view whenever it opens or the user switches the editing target.
  useEffect(() => {
    if (!showForm) return;
    const id = window.requestAnimationFrame(() => {
      if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [showForm, editingId]);

  // Clear success messages after 5 seconds; keep errors until user dismisses.
  useEffect(() => {
    if (message.text && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Fetch videos when filter changes
  useEffect(() => {
    fetchVideos();
  }, [filterType]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get video type display name
  const getVideoTypeDisplay = (type) => {
    switch (type) {
      case 'TUTORIAL':
        return 'Tutorial';
      case 'TESTIMONIAL':
        return 'Testimonial';
      case 'SHORTS':
        return 'YouTube Shorts';
      default:
        return type;
    }
  };

  // Get video type badge style
  const getVideoTypeBadgeStyle = (type) => {
    switch (type) {
      case 'TUTORIAL':
        return { backgroundColor: '#dbeafe', color: '#1e40af' };
      case 'TESTIMONIAL':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      default:
        return { backgroundColor: '#e5e7eb', color: '#374151' };
    }
  };

  const styles = {
    pageContainer: {
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    adminHeader: {
      background: '#1a2332',
      backgroundImage: `
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px, 30px 30px',
      padding: '2rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.1)'
    },
    adminHeaderContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    adminTitle: {
      fontSize: '2rem',
      fontWeight: '600',
      color: '#ffffff',
      margin: '0',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    },
    headerActions: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'center'
    },
    addButton: {
      backgroundColor: '#0ea5e9',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    filterContainer: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem'
    },
    filterButton: {
      padding: '0.5rem 1rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      backgroundColor: 'white',
      color: '#374151',
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    filterButtonActive: {
      backgroundColor: '#0ea5e9',
      color: 'white',
      borderColor: '#0ea5e9'
    },
    section: {
      padding: '2rem 0',
      flex: 1
    },
    container: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 2rem'
    },
    // Fixed top-center toast — visible regardless of scroll position.
    message: {
      position: 'fixed',
      top: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1500,
      padding: '0.875rem 1.25rem',
      borderRadius: '8px',
      fontWeight: '500',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.18)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      minWidth: '320px',
      maxWidth: 'min(640px, calc(100vw - 2rem))',
      animation: 'toastSlideDown 0.28s ease-out'
    },
    successMessage: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      border: '1px solid #a7f3d0'
    },
    errorMessage: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      border: '1px solid #fca5a5'
    },
    form: {
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      marginBottom: '2rem'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      transition: 'border-color 0.2s ease',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      backgroundColor: 'white',
      cursor: 'pointer',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '1rem',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    formActions: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'flex-end'
    },
    submitButton: {
      backgroundColor: submitting ? '#9ca3af' : '#0ea5e9',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: submitting ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease'
    },
    cancelButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    videoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem'
    },
    videoCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.2s ease'
    },
    videoPreview: {
      width: '100%',
      height: '200px',
      backgroundColor: '#000',
      borderRadius: '8px',
      marginBottom: '1rem',
      overflow: 'hidden'
    },
    videoIframe: {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '8px'
    },
    videoTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '0.5rem'
    },
    videoDescription: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '1rem',
      lineHeight: '1.4'
    },
    videoMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
      color: '#6b7280'
    },
    videoBadges: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem'
    },
    statusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    typeBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46'
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b'
    },
    videoActions: {
      display: 'flex',
      gap: '0.5rem'
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    editButton: {
      backgroundColor: '#f59e0b',
      color: 'white'
    },
    deleteButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    toggleButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    loading: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '200px',
      fontSize: '1.125rem',
      color: '#6b7280'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <div style={styles.loading}>Loading videos...</div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <style>{`
        @keyframes toastSlideDown {
          from { transform: translate(-50%, -120%); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
      `}</style>

      {/* Toast — rendered at root so it's not clipped by overflow:hidden parents */}
      {message.text && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          <span style={{ flex: 1 }}>{message.text}</span>
          <button
            onClick={() => setMessage({ type: '', text: '' })}
            aria-label="Dismiss"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, color: 'inherit', padding: '0 0.25rem' }}
          >×</button>
        </div>
      )}

      <section style={styles.adminHeader}>
        <div style={styles.adminHeaderContent}>
          <h1 style={styles.adminTitle}>Video Management</h1>
          <div style={styles.headerActions}>
            <button
              style={styles.addButton}
              onClick={() => setShowForm(true)}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0284c7'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#0ea5e9'}
            >
              Add New Video
            </button>
          </div>
        </div>
      </section>

      <main style={styles.section}>
        <div style={styles.container}>

          {/* Filter buttons */}
          <div style={styles.filterContainer}>
            {['ALL', 'TUTORIAL', 'TESTIMONIAL'].map(filter => (
              <button
                key={filter}
                style={{
                  ...styles.filterButton,
                  ...(filterType === filter ? styles.filterButtonActive : {})
                }}
                onClick={() => handleFilterChange(filter)}
              >
                {filter === 'ALL' ? 'All Videos' : getVideoTypeDisplay(filter)}
              </button>
            ))}
          </div>

          {showForm && (
            <div ref={formRef} style={styles.form}>
              <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>
                {editingId ? 'Edit Video' : 'Add New Video'}
              </h2>

              <div style={styles.formGroup}>
                <label style={styles.label}>Video Type *</label>
                <select
                  style={styles.select}
                  value={formData.videoType}
                  onChange={(e) => handleInputChange('videoType', e.target.value)}
                >
                  <option value="TUTORIAL">Tutorial</option>
                  <option value="TESTIMONIAL">Testimonial</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Title *</label>
                <input
                  style={styles.input}
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter video title"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>YouTube URL *</label>
                <input
                  style={styles.input}
                  type="url"
                  value={formData.youtubeUrl}
                  onChange={(e) => handleInputChange('youtubeUrl', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  style={styles.textarea}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter video description"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Display Order</label>
                <input
                  style={styles.input}
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleInputChange('displayOrder', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>

              <div style={styles.formGroup}>
                <div style={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                  <label style={styles.label}>Active</label>
                </div>
              </div>

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  style={styles.cancelButton}
                  onClick={resetForm}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  style={styles.submitButton}
                  onClick={handleSubmit}
                  disabled={submitting}
                  onMouseEnter={(e) => !submitting && (e.target.style.backgroundColor = '#0284c7')}
                  onMouseLeave={(e) => !submitting && (e.target.style.backgroundColor = '#0ea5e9')}
                >
                  {submitting ? 'Saving...' : (editingId ? 'Update Video' : 'Add Video')}
                </button>
              </div>
            </div>
          )}

          <div style={styles.videoGrid}>
            {videos.map((video) => (
              <div key={video.id} style={styles.videoCard}>
                <div style={styles.videoPreview}>
                  <iframe
                    style={styles.videoIframe}
                    src={`https://www.youtube.com/embed/${video.embedId}`}
                    title={video.title}
                    allowFullScreen
                  />
                </div>
                
                <h3 style={styles.videoTitle}>{video.title}</h3>
                
                {video.description && (
                  <p style={styles.videoDescription}>{video.description}</p>
                )}

                <div style={styles.videoMeta}>
                  <span>Order: {video.displayOrder}</span>
                </div>

                <div style={styles.videoBadges}>
                  <span 
                    style={{
                      ...styles.statusBadge,
                      ...(video.isActive ? styles.activeBadge : styles.inactiveBadge)
                    }}
                  >
                    {video.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span 
                    style={{
                      ...styles.typeBadge,
                      ...getVideoTypeBadgeStyle(video.videoType)
                    }}
                  >
                    {getVideoTypeDisplay(video.videoType)}
                  </span>
                </div>

                <div style={styles.videoActions}>
                  <button
                    style={{...styles.actionButton, ...styles.editButton}}
                    onClick={() => handleEdit(video)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                  >
                    Edit
                  </button>
                  <button
                    style={{...styles.actionButton, ...styles.toggleButton}}
                    onClick={() => handleToggleStatus(video.id)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                  >
                    {video.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    style={{...styles.actionButton, ...styles.deleteButton}}
                    onClick={() => handleDelete(video.id)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {videos.length === 0 && !loading && (
            <div style={styles.loading}>
              No videos found. Click "Add New Video" to get started.
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminVideoUpload;







